<template>
  <div :class="$style.wrap">
    <template v-if="isEcomUser">
      <aw-product-list
        :is-whole-loading-completed="isWholeLoadingCompleted"
        :is-more-loading-completed="isMoreLoadingCompleted"
        :item-count="itemCount ?? undefined"
        @load-more-product-items="loadMoreProductItems"
        @order-product-items="orderProductItems"
        @filter-product-items="filterProductItems"
      >
        <template #content>
          <aw-product-list-header v-if="itemCount" :product-counter="{ all: itemCount, loaded: products.length }">
            <template #listName>
              <lv-heading
                tag="h1"
                level="2"
                version2
                no-gutter
              >
                {{ $awt('aw.previously_ordered_products.tab_title') }}
              </lv-heading>
            </template>
            <template #filters>
              <client-only>
                <aw-subcategory-filter
                  v-if="screenRange['md-max']"
                  view="FULL"
                  @activate-filter="filterProductItems()"
                />
              </client-only>
            </template>
          </aw-product-list-header>
        </template>
      </aw-product-list>
      <aw-content-seo-text
        v-if="getCategoryFromTree && getCategoryFromTree.seoTextId"
        :content-id="getCategoryFromTree.seoTextId"
      />
    </template>
    <template v-else>
      <aw-profile-empty />
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
  import { refreshNuxtData, useAsyncData, useNuxtApp } from 'nuxt/app';
  import { fetchKey } from '~~/common/utils/awRunLegacyAsyncData';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import AwProductList from '~~/common/components/Page/ProductList/AwProductList.vue';
  import AwProductListHeader from '~~/common/components/Page/ProductList/AwProductListHeader.vue';
  import AwProfileEmpty from '~~/common/components/Common/AwProfileEmpty.vue';
  import {
    useIsReturningEnabled,
    useIsReturningFromProductPage,
  } from '~~/common/composables/useProductListHistory';
  import { createScrollToProductId } from '~~/common/utils/productListHistoryScroll';

  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useCategoryStore } from '~~/common/stores/category';
  import { useProductsStore, type ProductQueryParams } from '~~/common/stores/products';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import { useUserStore } from '~~/common/stores/user';
  import { isApiError } from '~~/common/utils/apiError';
  import { defineI18nRoute } from '#i18n';

  const AwSubcategoryFilter = defineAsyncComponent(() => import('~~/common/components/Page/ProductList/AwSubcategoryFilter.vue'));
  const AwContentSeoText = defineAsyncComponent(() => import('~~/shop/app/components/AwContentSeoText.vue'));

  defineOptions({
    name: 'ProfileOrderAgainPage',
  });

  // eslint-disable-next-line no-undef
  definePageMeta({
    layout: 'dynamic-content',
  });
  false && /*#__PURE__*/ defineI18nRoute({
    paths: {
      en: '/shop/profile/order-again',
      hu: '/shop/profilom/gyakori-termekek',
    },
  });

  const { $logger } = useNuxtApp();
  const userInterfaceStore = useUserInterfaceStore();
  const categoryStore = useCategoryStore();
  const productsStore = useProductsStore();
  const deliveryStore = useDeliveryStore();
  const userStore = useUserStore();

  const isReturningFromProductPage = useIsReturningFromProductPage();
  const isReturningEnabled = useIsReturningEnabled();

  await useAsyncData(
    fetchKey(),
    async () => {
      if (userStore.isEcomUser) {
        try {
          if (isReturningFromProductPage.value && isReturningEnabled.value) {
            productsStore.restoreFromProductListHistoryInformation();
          } else {
            await productsStore.fetchProducts({
              isCached: false,
              page: 1,
              itemsPerPage: productsStore.itemsPerPage || 16,
              filterParams: {
                available: 1,
              },
              allPreviouslyOrdered: true,
            });
          }
        } catch (error) {
          if (!isApiError(error) || error.response?.status !== 404) {
            $logger.error(error);
          }
        }
      }
      return {};
    },
  );

  const isWholeLoadingCompleted = ref(true);
  const isMoreLoadingCompleted = ref(true);

  const getCategoryFromTree = computed(() => categoryStore.getCategoryFromTree);
  const screenRange = computed(() => userInterfaceStore.mediaQueries);
  const itemsPerPage = computed(() => productsStore.itemsPerPage);
  const currentPage = computed(() => productsStore.currentPage);
  const itemCount = computed(() => productsStore.itemCount);
  const sortKey = computed(() => productsStore.sortKey);
  const sortBy = computed(() => productsStore.sortBy);
  const filterBy = computed(() => productsStore.filterBy);
  const products = computed(() => productsStore.renderedProducts);
  const deliverySetup = computed(() => deliveryStore.setup);
  const isEcomUser = computed(() => userStore.isEcomUser);

  const productBaseQuery = computed<ProductQueryParams>(() => {
    const data: ProductQueryParams = {
      isCached: false,
      itemsPerPage: itemsPerPage.value,
      page: currentPage.value,
      filterParams: filterBy.value,
      allPreviouslyOrdered: true,
    };
    if (sortKey.value) {
      data.sortKey = sortKey.value;
    }
    if (sortBy.value) {
      data.sortBy = sortBy.value;
    }
    return data;
  });

  watch(deliverySetup, () => {
    refreshNuxtData();
  });

  onMounted(() => {
    categoryStore.setTreeId(null);
    const productId = productsStore.productListHistoryInformation?.productId;
    if (isReturningFromProductPage.value && productId !== undefined) {
      const scrollToProductId = createScrollToProductId();
      scrollToProductId(productId);
    }
  });

  async function loadMoreProductItems (loadingState?: { complete: () => void; loaded: () => void }) {
    isMoreLoadingCompleted.value = false;

    if (loadingState && itemCount.value === products.value.length) {
      loadingState.complete();
      return;
    }

    const result = await productsStore.fetchProducts({
      ...productBaseQuery.value,
      page: currentPage.value + 1,
    });

    isMoreLoadingCompleted.value = true;
    if (loadingState) {
      if (!result || itemCount.value === products.value.length) {
        loadingState.complete();
      } else {
        loadingState.loaded();
      }
    }
  }
  async function orderProductItems (orderParams: { value: ProductQueryParams }) {
    isMoreLoadingCompleted.value = false;
    let data: ProductQueryParams = {
      ...productBaseQuery.value,
      itemsPerPage: currentPage.value * (itemsPerPage.value ?? 0),
      page: 1,
    };

    if (sortKey.value) {
      delete data.sortKey;
    }
    if (sortBy.value) {
      delete data.sortBy;
    }

    data = {
      ...data,
      ...orderParams.value,
    };

    await productsStore.fetchProducts(data);
    isMoreLoadingCompleted.value = true;
  }
  async function filterProductItems () {
    isMoreLoadingCompleted.value = false;
    await productsStore.fetchProducts({
      ...productBaseQuery.value,
      itemsPerPage: currentPage.value * (itemsPerPage.value ?? 0),
      page: 1,
    });
    isMoreLoadingCompleted.value = true;
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.promotion {
  @include lg() {
    margin-bottom: 27px;
  }
  margin: 23px 0 18px;
}

.headline {
  margin-top: 12px;
  margin-bottom: 20px;
}
</style>
